import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse } from '@shared/types';

/**
 * axios 实例配置
 * baseURL 从环境变量读取，配置请求/响应拦截器
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：自动附加JWT Token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器：统一处理响应格式和错误
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const res = response.data;

    // 文件流响应（导出Excel等）直接返回
    if (response.config.responseType === 'blob') {
      return response as any;
    }

    // 业务成功
    if (res.code === 0) {
      return res as any;
    }

    // 业务错误：统一提示
    const error = new Error(res.message || '请求失败') as any;
    error.code = res.code;
    error.response = response;
    return Promise.reject(error);
  },
  (error) => {
    // HTTP错误处理
    if (error.response) {
      const { status, data } = error.response;
      const apiError = data as ApiResponse | undefined;

      // 401未认证：清除token跳转登录
      if (status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login' && window.location.pathname !== '/m/login') {
          window.location.href = '/login';
        }
      }

      const msg = apiError?.message || `请求错误 (${status})`;
      return Promise.reject(Object.assign(new Error(msg), { code: apiError?.code || status }));
    }

    if (error.request) {
      return Promise.reject(new Error('网络错误，请检查网络连接'));
    }

    return Promise.reject(error);
  },
);

export default apiClient;
